import React from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export default function CartDrawer() {
  const {
    cart,
    cartOpen,
    setCartOpen,
    updateQty,
    removeFromCart,
    formatPrice,
    cartTotalInr,
    user,
    setAuthModalOpen,
  } = useApp();
  const navigate = useNavigate();

  const checkout = () => {
    setCartOpen(false);
    if (!user) {
      setAuthModalOpen(true);
      return;
    }
    navigate("/checkout");
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle className="font-display flex items-center gap-2">
            <ShoppingBag size={20} /> Your Bag ({cart.length})
          </SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-6 gap-3">
            <ShoppingBag size={48} className="text-slate-300" />
            <p className="text-slate-500">Your bag is empty</p>
            <Button
              onClick={() => {
                setCartOpen(false);
                navigate("/shop");
              }}
              className="bg-[#E63946] hover:bg-[#d12836]"
            >
              Start Shopping
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {cart.map((item, i) => (
                <div key={i} className="flex gap-3" data-testid={`cart-item-${i}`}>
                  <img
                    src={item.product.images?.[0]}
                    alt={item.product.name}
                    className="w-20 h-24 object-cover rounded-lg bg-slate-100"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm line-clamp-1">{item.product.name}</h4>
                    <p className="text-xs text-slate-400">
                      {item.size} • {item.color} • {item.logo ? "With logo" : "No logo"}
                    </p>
                    <p className="font-mono-price font-bold text-sm mt-1">
                      {formatPrice(item.product.price)}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQty(i, -1)}
                        className="w-7 h-7 rounded-md border flex items-center justify-center hover:bg-slate-100"
                        data-testid={`cart-decrease-${i}`}
                      >
                        <Minus size={13} />
                      </button>
                      <span className="w-6 text-center text-sm font-semibold">{item.quantity}</span>
                      <button
                        onClick={() => updateQty(i, 1)}
                        className="w-7 h-7 rounded-md border flex items-center justify-center hover:bg-slate-100"
                        data-testid={`cart-increase-${i}`}
                      >
                        <Plus size={13} />
                      </button>
                      <button
                        onClick={() => removeFromCart(i)}
                        className="ml-auto text-slate-400 hover:text-red-500"
                        data-testid={`cart-remove-${i}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t px-6 py-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-mono-price font-bold text-lg">{formatPrice(cartTotalInr)}</span>
              </div>
              <Button
                data-testid="checkout-proceed-button"
                onClick={checkout}
                className="w-full bg-[#E63946] hover:bg-[#d12836] h-12 text-base font-semibold"
              >
                Proceed to Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
